// filepath: apps/web/src/runtime/apiRuntime.ts
//
// ISS-377 (uat-ba note_13124) — runtime configuration for the
// SPA API client.
//
// The pre-ISS-377 silent-refresh interceptor was hard-coded to the
// `*.platform.example` regex (see `apps/web/src/auth/silentRefresh.ts:46`).
// That worked for the dev/staging platform.example deployments but
// failed on every other host (`uat-ascoa-go.askou.org`, the RZT
// UAT hosts, the apex operator host, a custom-cname production
// host) — a 401 on those hosts was never seen by the interceptor,
// the OIDC client never refreshed, and every authenticated call
// from then on returned 401 forever.
//
// The fix: the trigger host is derived from the resolved
// `TenantConfig.realm_url` (the Keycloak realm host) AND the
// current `window.location.host`, joined with an OR. A request
// whose host matches either is a "trigger host". This is a strict
// superset of the legacy `*.platform.example` regex and works for
// every deployment.
//
//   - `uat-ascoa-go.askou.org` -> `window.location.host` matches
//     itself.
//   - `*.rzt.platform.example` -> matches via the
//     `*.platform.example` suffix the BA's original intent encoded.
//   - `acme.ascoa.io` (custom cname) -> matches via the current
//     origin.
//
// `apps/web/src/runtime/` is the canonical home for cross-cutting
// runtime concerns (focusTrap, safeRichText, useIsMobile). This
// module slots in next to `useIsMobile.ts` with the same test
// pattern (`apiRuntime.test.ts` + the allowlist update in
// `staticGuards.test.ts`).

import type { TenantConfig } from "@/auth/types";

/**
 * Internal: the currently-resolved tenant config. Set by
 * `setRuntimeTenantConfig` (called from `AppBootstrap` after
 * `useTenantConfig` resolves). Reads BEFORE the bootstrap
 * completes (i.e. before `setRuntimeTenantConfig` is called)
 * fall back to the `window.location.host` derivation.
 */
let cachedTenantConfig: TenantConfig | null = null;

/**
 * Set the resolved tenant config. Called from `AppBootstrap`
 * (or any successor that owns the bootstrap step) on every
 * successful `/api/tenant-config` resolution. Subsequent calls
 * overwrite the cached value — a tenant switch (different
 * `realm_url`) updates the trigger host atomically.
 */
export function setRuntimeTenantConfig(config: TenantConfig | null): void {
  cachedTenantConfig = config;
}

/**
 * Read the cached tenant config. Test-only escape hatch — the
 * production code path is `setRuntimeTenantConfig` (writer) and
 * the three `get*` readers (consumers). Exported for tests so
 * `apiRuntime.test.ts` can assert the cached value without going
 * through the React tree.
 */
export function __getRuntimeTenantConfig(): TenantConfig | null {
  return cachedTenantConfig;
}

/**
 * Reset the runtime config to the un-initialised state. Test-only
 * escape hatch; mirrors the `__resetOperatorTokenForTests` pattern
 * so per-test setup does not leak.
 */
export function __resetRuntimeConfigForTests(): void {
  cachedTenantConfig = null;
}

/**
 * Internal: derive a host from a URL string, or return `null`
 * if the URL is unparseable.
 */
function hostFromUrl(url: string | null | undefined): string | null {
  if (typeof url !== "string" || url.length === 0) return null;
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

/**
 * Resolve the current `window.location.host`. Returns `null`
 * outside a browser (SSR, unit-test jsdom without a window).
 */
function currentWindowHost(): string | null {
  if (typeof window === "undefined") return null;
  return window.location.host;
}

/**
 * Resolve the silent-refresh trigger host.
 *
 *   - If the runtime config has a `realm_url` (set by
 *     `setRuntimeTenantConfig`), the host of that URL is the
 *     primary trigger. This binds the interceptor to the same
 *     realm the OIDC client uses.
 *   - Otherwise, the current `window.location.host` is the
 *     fallback. This covers the bootstrap window
 *     (BEFORE `setRuntimeTenantConfig` is called) AND the
 *     apex operator host (which has no tenant config).
 *
 * Returns `null` only when both derivations fail (SSR / no
 * window). The caller (`apiFetch`) treats `null` as "no
 * trigger host" — the interceptor skips the silent-refresh
 * path entirely.
 */
export function getSilentRefreshTriggerHost(): string | null {
  const realmHost = hostFromUrl(cachedTenantConfig?.realm_url);
  if (realmHost !== null) return realmHost;
  return currentWindowHost();
}

/**
 * Resolve the API base path. Defaults to `/api` (same-origin
 * tenant-scoped endpoints). The runtime config MAY override this
 * in the future (e.g. for the apex operator surface pointing at
 * a different origin); v1 ships with the `/api` default.
 */
export function getApiBase(): string {
  // Future: read a per-tenant override from cachedTenantConfig.
  return "/api";
}

/**
 * The legacy `*.platform.example` regex preserved as a
 * documented default. New code MUST go through
 * `getSilentRefreshTriggerHost()`; this regex is exported for
 * the migration / test-ergonomics path only. The hard-coded
 * fallback that previously lived at `silentRefresh.ts:46`
 * (the `/\.platform\.example$/i` default) is now derived from
 * the runtime config — `silentRefresh.ts` no longer falls back
 * to this regex; the runtime config IS the source of truth.
 */
export const LEGACY_PLATFORM_EXAMPLE_RE: RegExp = /\.platform\.example$/i;

/**
 * `doesHostMatchTrigger` — internal helper. Exposed for tests
 * so the `apiRuntime.test.ts` and `silentRefresh.test.ts` META
 * controls can assert the host matching predicate without
 * re-implementing the same logic in two places. The shape is
 * "either exact match or suffix match on the trigger host"
 * (covers `acme.platform.example` matching the
 * `*.platform.example` legacy default).
 */
export function doesHostMatchTrigger(
  requestHost: string | null,
  triggerHost: string | null,
): boolean {
  if (requestHost === null || triggerHost === null) return false;
  if (requestHost === triggerHost) return true;
  if (requestHost.endsWith("." + triggerHost)) return true;
  if (triggerHost.endsWith("." + requestHost)) return true;
  return false;
}
